import firebase from 'firebase/app'
import { shuffle } from '@/utils'
import { Game } from '@/class/game'
import { RoomMember, RoomMemberData } from '@/class/roomMember'
import { RoleId, RoomState, GameState } from '@/enum'

export interface RoomData {
  state: RoomState;
  playingGameId: string;
  masterId: string;
  unselectedPriorityRolePlayerIds: string[];
  members: {[id: string]: RoomMemberData};
  userInterfaceType: 'A' | 'B';
  settings: RoomSettings;
  resultLogs: ResultLog[];
}

export interface RoomSettings {
  mustUseRoles: RoleId[];
  randomUseRoles: RoleId[];
  timerSettings: {[key: string]: number};
  roleSettings: RoleSettings;
  canNightCommunication: boolean;
  isFullclose: boolean;
  canGallaryFullopen: boolean;
  isFirstNightRandomWhite: boolean;
  existsCrabShield: boolean;
  dayVote: boolean;
  enableFinalVote: boolean;
  enableRundomExecution: boolean;
}

export interface RoleSettings {
  bullet: number;
  armor: number;
  needSteal: number;
}

export interface ReadyPlayer {
  id: string;
  name: string;
  selectablePriorityRole: RoleId[];
  selectedRoleId: RoleId | null;
}

export interface Regulation {
  playersCount: number;
  settings: RoomSettings;
}

export interface ResultLog {
  gameId: string;
  winnerPlayerIds: string[];
  winnerRoleIds: RoleId[];
  playerRoles: {[key: string]: RoleId};
}

export const ROOM_SETTING = {
  mustUseRoles: [],
  randomUseRoles: [],
  canNightCommunication: true,
  isFullclose: false,
  canGallaryFullopen: true,
  timerSettings: {
    [GameState.ready]: 30,
    [GameState.firstNight]: 60,
    dayByLiver: 60,
    dayMax: 420,
    [GameState.voteStart]: 30,
    [GameState.voteEnd]: 15,
    [GameState.finalVoteReady]: 60,
    [GameState.finalVoteStart]: 30,
    [GameState.takeOther]: 30,
    [GameState.night]: 45
  },
  roleSettings: {
    bullet: 2,
    armor: 1,
    needSteal: 2
  },
  existsCrabShield: true,
  isFirstNightRandomWhite: false,
  dayVote: true,
  enableFinalVote: true,
  enableRundomExecution: true
}

const DEFAULT_ROOM_SETTING: RoomData = {
  state: RoomState.standBy,
  masterId: '',
  playingGameId: '',
  unselectedPriorityRolePlayerIds: [],
  members: {},
  userInterfaceType: 'A',
  settings: ROOM_SETTING,
  resultLogs: []
}

export class Room {
  public id = ''
  public room: RoomData = DEFAULT_ROOM_SETTING
  public game: Game = {} as Game
  public memberOf: {[id: string]: RoomMemberData} = {}
  public selfId: string | undefined
  public self: RoomMember | null = null

  constructor (roomId?: string, playerId?: string) {
    if (roomId) this.init(roomId)
    if (playerId) this.selfId = playerId
  }

  public async createRoom (newMemberName: string) {
    const { id: roomId } = await firebase.firestore().collection('room').add(DEFAULT_ROOM_SETTING)
    this.init(roomId)

    const memberId = await this.addRoomMaster(newMemberName)
    this.selfId = memberId

    return memberId
  }

  private init (id: string) {
    this.id = id
    this.startObserveRoom()
  }

  private startObserveRoom () {
    this.roomDocRef.onSnapshot((snapshot) => {
      this.room = snapshot.data() as RoomData
      this.memberOf = {}
      Object.entries(this.room.members).map(([memberId, member]) => {
        this.memberOf[memberId] = member as RoomMemberData
      })

      if (this.selfId) {
        this.setSelf(this.selfId)
      }

      if (this.room.playingGameId && this.game.id !== this.room.playingGameId) {
        this.game = new Game(this)
        this.game.init(this.room.playingGameId)
      }
    })
  }

  public get members (): RoomMemberData[] {
    return Object.values(this.memberOf).sort((a, b) => a.id > b.id ? 1 : -1)
  }

  public get readyMembers (): RoomMemberData[] {
    return this.members.filter(member => member.ready)
  }

  public getRoomMember (playerId: string): RoomMemberData {
    return this.memberOf[playerId]
  }

  private async addRoomMaster (name: string) {
    const { id: memberId } = await this.memberCollectoion.add({ name })

    this.room.members[memberId] = this.createMemberData(memberId, name)

    this.masterId = memberId

    await this.updateRoom()

    return memberId
  }

  public async addRoomMember (name: string) {
    const { id: memberId } = await this.memberCollectoion.add({ name })

    const key = `members.${memberId}`

    const data = {
      [key]: this.createMemberData(memberId, name)
    }

    await this.roomDocRef.update(data)

    return memberId
  }

  private createMemberData (id: string, name: string) {
    return {
      id,
      name,
      ready: false
    }
  }

  public removeRoomMember (id: string) {
    delete this.room.members[id]
    this.updateRoom()
    this.memberCollectoion.doc(id).delete()
  }

  public setSelf (playerId: string) {
    this.selfId = playerId
    this.self = new RoomMember(this.id, this.getRoomMember(playerId))
  }

  public get masterId (): string {
    return this.room.masterId
  }

  public set masterId (id) {
    this.room.masterId = id
  }

  public get userInterfaceType () {
    return this.room.userInterfaceType
  }

  public set userInterfaceType (type) {
    this.room.userInterfaceType = type
  }

  public get settings () {
    return this.room.settings
  }

  public setMustUseRoles (mustUseRoles: RoleId[]) {
    this.room.settings.mustUseRoles = mustUseRoles
    this.updateRoom()
  }

  public setRandomUseRoles (randomUseRoles: RoleId[]) {
    this.room.settings.randomUseRoles = randomUseRoles
    this.updateRoom()
  }

  public updateRoomMaster (id: string) {
    this.masterId = id
    this.updateRoom()
  }

  public get isLoading (): boolean {
    return this.room.state === RoomState.loading
  }

  public get isStandBy (): boolean {
    return this.room.state === RoomState.standBy
  }

  public get isPlaying (): boolean {
    return this.room.state === RoomState.playing
  }

  public setGameId (gameId: string) {
    this.room.playingGameId = gameId
  }

  public crearGameId () {
    this.room.playingGameId = ''
  }

  private get roomDocRef (): firebase.firestore.DocumentReference {
    return firebase.firestore().doc(`room/${this.id}`)
  }

  public get memberCollectoion (): firebase.firestore.CollectionReference {
    return firebase.firestore().collection(`room/${this.id}/member`)
  }

  public updateRoom (): void {
    this.roomDocRef.update(this.room)
  }

  public get unselectedPriorityRolePlayerIds () {
    return this.room.unselectedPriorityRolePlayerIds
  }

  public set unselectedPriorityRolePlayerIds (playerIds: string[]) {
    this.room.unselectedPriorityRolePlayerIds = playerIds
  }

  public resetUnselectedPriorityRolePlayerIds () {
    this.unselectedPriorityRolePlayerIds = shuffle(this.members.map(member => member.id))
  }

  public get regulation (): Regulation {
    return {
      playersCount: this.readyMembers.length,
      settings: this.settings
    }
  }

  public get resultLogs () {
    return this.room.resultLogs
  }

  public async newGame () {
    this.game = new Game(this)
    const gameID = await this.game.newGame(this, this.newPlayers())
    this.setGameId(gameID)
    this.room.state = RoomState.playing
    this.updateRoom()
  }

  public resetGame () {
    this.crearGameId()
    this.members.forEach(member => { member.ready = false })
    this.room.state = RoomState.standBy
    this.updateRoom()
  }

  private newPlayers () {
    const players: { [id: string]: ReadyPlayer } = {}

    // 優先役職選択者を選定
    const roleSelectablePlayerIds = this.pickRoleSelectablePlayerIds()

    // 選択可能役職を選定
    const selectableRole = this.pickSelectableRole()
    const selectableRoleCount = this.readyMembers.length / 3

    this.readyMembers.forEach(member => {
      const selectablePriorityRole =
        roleSelectablePlayerIds.some(id => id === member.id)
          ? selectableRole.splice(0, selectableRoleCount)
          : []

      players[member.id] = {
        id: member.id,
        name: member.name,
        selectablePriorityRole,
        selectedRoleId: null
      }
    })

    return players
  }

  private pickRoleSelectablePlayerIds () {
    const roleSelectablePlayerIds = []

    // 役職選択可能な人数
    const selectablePlayerCount = this.readyMembers.length / 4

    for (let i = 0; i < selectablePlayerCount; i++) {
      // 優先役職選択をしたことがない人を抽出
      if (this.unselectedPriorityRolePlayerIds.length === 0) {
        this.resetUnselectedPriorityRolePlayerIds()
      }
      const randomIndex = Math.floor(Math.random() * this.unselectedPriorityRolePlayerIds.length)
      const playerId = this.unselectedPriorityRolePlayerIds.splice(randomIndex, 1)[0]

      // 部屋に選出されたプレイヤーが存在するかどうか
      if (this.readyMembers.some(member => member.id === playerId)) {
        roleSelectablePlayerIds.push(playerId)
      } else {
        // 部屋からいなくなったプレイヤーが選出された場合は再抽選を行う
        i--
      }
    }

    return roleSelectablePlayerIds
  }

  private pickSelectableRole () {
    const memberCount = this.readyMembers.length

    return shuffle(this.settings.mustUseRoles).concat(shuffle(this.settings.randomUseRoles))
      .splice(0, memberCount)
  }

  public pushResultLog (resultLog: ResultLog) {
    this.room.resultLogs.push(resultLog)
    this.updateRoom()
  }
}
